'use client';

import { useEffect } from 'react';
import Router from 'next/router';
import { useBodyClasses } from '@/hooks/useBodyClasses';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { initializeGoogleTagManager } from '@/utils/googleTagManager';
import { CONFIG } from '@/constants/config';
import { noTopInsert } from '@/bridge/utils/noTopInsert';
import { useLoginUser } from '@/hooks/useLoginUser';
import { useFirstInteraction } from '@/hooks/useFirstInteraction';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import { useAdriverBgBannerCleaner } from '@/components/Banners/AdriverBanner.hooks';

interface ClientWrapperProps {
  children: React.ReactNode;
  isSSRWebView?: boolean;
  accessToken?: string;
}

export function ClientWrapper({
  children,
  isSSRWebView,
  accessToken,
}: ClientWrapperProps) {
  useBodyClasses();
  useBackNavigation();
  useAdriverBgBannerCleaner();
  const isLoginInProgress = useLoginUser(accessToken);
  const [setCanLoadBanners, canLoadBanners] = useBannerStore(state => [
    state.setCanLoadBanners,
    state.canLoadBanners,
  ]);

  useFirstInteraction(() => setCanLoadBanners(true));

  useEffect(() => {
    if (canLoadBanners) {
      console.log('Loading scripts for gtm');
      initializeGoogleTagManager(CONFIG.gtmContainerId);
    }

    if (isSSRWebView) {
      noTopInsert();
      Router.events.on('beforeHistoryChange', noTopInsert);
    }

    if (!document.querySelector('script[src*="gpt.js"]') && canLoadBanners) {
      console.log('Loading scripts for gpt');
      const script = document.createElement('script');
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (isSSRWebView) {
        Router.events.off('beforeHistoryChange', noTopInsert);
      }
    };
  }, [isSSRWebView, canLoadBanners]);

  if (isLoginInProgress) return null;

  return <>{children}</>;
}
